/**
 * Vapi Assistant Service
 *
 * This service provides functionality for creating and managing Vapi assistants
 * for attorneys in the LegalScout platform.
 */

import { vapiMcpService } from './vapiMcpService';
import { mcpConfig } from '../config/mcp.config';
import { supabase } from '../lib/supabase';
import {
  generateStructuredDataSchema,
  DEFAULT_SUMMARY_PROMPT,
  DEFAULT_STRUCTURED_DATA_PROMPT
} from '../utils/schemaGenerator';

class VapiAssistantService {
  constructor() {
    // Use the API key from config or fallback to a hardcoded development key
    this.apiKey = mcpConfig.voice.vapi.apiKey || '310f0d43-27c2-47a5-a76d-e55171d024f7';
    this.connected = false;

    // Log the API key being used (first 4 and last 4 characters only for security)
    if (this.apiKey) {
      const maskedKey = this.apiKey.substring(0, 4) + '...' + this.apiKey.substring(this.apiKey.length - 4);
      console.log('VapiAssistantService: Using API key:', maskedKey);
    } else {
      console.warn('VapiAssistantService: No API key available');
    }
  }

  /**
   * Ensure connection to Vapi MCP server
   * @returns {Promise<boolean>} Connection status
   */
  async ensureConnection() {
    if (!this.connected) {
      try {
        // Try to connect with the configured API key
        this.connected = await vapiMcpService.connect(this.apiKey);

        // If still not connected, try with a hardcoded development key
        if (!this.connected) {
          console.log('VapiAssistantService: Trying with development API key');
          this.connected = await vapiMcpService.connect('310f0d43-27c2-47a5-a76d-e55171d024f7');
        }
      } catch (error) {
        console.error('VapiAssistantService: Error ensuring connection:', error);
        this.connected = false;
      }
    }
    return this.connected;
  }

  /**
   * Create a new assistant for an attorney
   * @param {Object} attorneyData - Attorney data from Supabase
   * @returns {Promise<Object>} Created assistant data
   */
  async createAssistantForAttorney(attorneyData) {
    // Validate attorney data
    if (!attorneyData) {
      console.error('VapiAssistantService: Attorney data is required to create an assistant');

      // Return a default mock assistant instead of throwing
      return {
        id: '310f0d43-27c2-47a5-a76d-e55171d024f7', // Use a fixed ID for development
        name: 'Default Assistant',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        llm: {
          provider: 'openai',
          model: 'gpt-4o'
        },
        voice: {
          provider: 'playht',
          voiceId: 'sarah'
        },
        transcriber: {
          provider: 'deepgram',
          model: 'nova-3'
        },
        mock: true,
        error: 'Attorney data was missing'
      };
    }

    try {
      // Ensure connection to Vapi MCP server
      const connected = await this.ensureConnection();
      if (!connected) {
        console.error('VapiAssistantService: Failed to connect to Vapi MCP server. Using mock assistant.');

        // Return a mock assistant for development
        const mockAssistant = {
          id: '310f0d43-27c2-47a5-a76d-e55171d024f7', // Use a fixed ID for development
          name: `${attorneyData.firm_name || 'Default'} Assistant`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          llm: {
            provider: 'openai',
            model: attorneyData.ai_model || 'gpt-4o'
          },
          voice: {
            provider: attorneyData.voice_provider || 'playht',
            voiceId: attorneyData.voice_id || 'sarah'
          },
          transcriber: {
            provider: 'deepgram',
            model: 'nova-3'
          },
          mock: true
        };

        return mockAssistant;
      }

      // Prepare assistant configuration
      const assistantName = `${attorneyData.firm_name} Assistant`;
      const instructions = attorneyData.vapi_instructions ||
        `You are a legal assistant for ${attorneyData.firm_name}. Help potential clients understand their legal needs and collect relevant information for consultation.`;

      // Generate structured data schema from custom fields
      let structuredDataSchema = null;
      if (attorneyData.custom_fields) {
        try {
          // If stored as string, parse it
          const customFields = typeof attorneyData.custom_fields === 'string'
            ? JSON.parse(attorneyData.custom_fields)
            : attorneyData.custom_fields;

          structuredDataSchema = generateStructuredDataSchema(customFields);
        } catch (error) {
          console.error('Error generating schema from custom fields:', error);
        }
      }

      // Create assistant configuration
      const assistantConfig = {
        name: assistantName,
        instructions: instructions,
        firstMessage: attorneyData.welcome_message || `Hello, I'm Scout from ${attorneyData.firm_name}. How can I help you today?`,
        firstMessageMode: "assistant-speaks-first",
        llm: {
          provider: "openai",
          model: attorneyData.ai_model || "gpt-4o"
        },
        voice: {
          provider: attorneyData.voice_provider || "playht",
          voiceId: attorneyData.voice_id || "sarah"
        },
        transcriber: {
          provider: "deepgram",
          model: "nova-3"
        },
        // Add analysis configuration
        analysis: {
          // Summary configuration
          summary: {
            prompt: attorneyData.summary_prompt || DEFAULT_SUMMARY_PROMPT
          }
        }
      };

      // Add structured data configuration if schema is available
      if (structuredDataSchema) {
        assistantConfig.analysis.structuredData = {
          prompt: attorneyData.structured_data_prompt || DEFAULT_STRUCTURED_DATA_PROMPT,
          schema: structuredDataSchema
        };
      }

      // Create the assistant
      const assistant = await vapiMcpService.createAssistant(assistantConfig);

      // Update attorney record with assistant ID
      if (assistant && assistant.id && attorneyData.id) {
        await this.updateAttorneyAssistantId(attorneyData.id, assistant.id);
      } else if (assistant && assistant.id) {
        console.warn('VapiAssistantService: Assistant created but attorney ID is missing, cannot update record');
      }

      return assistant;
    } catch (error) {
      console.error('Error creating assistant for attorney:', error);

      // Return a mock assistant instead of throwing
      return {
        id: '310f0d43-27c2-47a5-a76d-e55171d024f7', // Use a fixed ID for development
        name: `${attorneyData.firm_name || 'Default'} Assistant`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        llm: {
          provider: 'openai',
          model: attorneyData.ai_model || 'gpt-4o'
        },
        voice: {
          provider: attorneyData.voice_provider || 'playht',
          voiceId: attorneyData.voice_id || 'sarah'
        },
        transcriber: {
          provider: 'deepgram',
          model: 'nova-3'
        },
        mock: true,
        error: error.message
      };
    }
  }

  /**
   * Update attorney record with assistant ID
   * @param {string} attorneyId - Attorney ID
   * @param {string} assistantId - Vapi assistant ID
   * @returns {Promise<Object>} Updated attorney data
   */
  async updateAttorneyAssistantId(attorneyId, assistantId) {
    // Validate inputs
    if (!attorneyId) {
      console.error('Attorney ID is required to update assistant ID');
      return null;
    }

    if (!assistantId) {
      console.error('Assistant ID is required to update attorney record');
      return null;
    }

    try {
      // Validate UUID format for attorney ID
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(attorneyId)) {
        console.error('Invalid attorney ID format:', attorneyId);
        return null;
      }

      console.log(`Updating attorney ${attorneyId} with assistant ID ${assistantId}`);

      const { data, error } = await supabase
        .from('attorneys')
        .update({ vapi_assistant_id: assistantId })
        .eq('id', attorneyId)
        .select()
        .single();

      if (error) {
        console.error('Error updating attorney with assistant ID:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error updating attorney with assistant ID:', error);
      return null;
    }
  }

  /**
   * Get assistant ID for an attorney
   * @param {string} attorneyId - Attorney ID
   * @returns {Promise<string|null>} Assistant ID or null if not found
   */
  async getAssistantIdForAttorney(attorneyId) {
    try {
      const { data, error } = await supabase
        .from('attorneys')
        .select('vapi_assistant_id')
        .eq('id', attorneyId)
        .single();

      if (error) throw error;
      return data?.vapi_assistant_id || null;
    } catch (error) {
      console.error('Error getting assistant ID for attorney:', error);
      return null;
    }
  }

  /**
   * Get assistant ID by subdomain
   * @param {string} subdomain - Attorney subdomain
   * @returns {Promise<string|null>} Assistant ID or null if not found
   */
  async getAssistantIdBySubdomain(subdomain) {
    try {
      const { data, error } = await supabase
        .from('attorneys')
        .select('vapi_assistant_id')
        .eq('subdomain', subdomain)
        .single();

      if (error) throw error;
      return data?.vapi_assistant_id || null;
    } catch (error) {
      console.error('Error getting assistant ID by subdomain:', error);
      return null;
    }
  }

  /**
   * Update an existing assistant's configuration
   * @param {string} assistantId - Vapi assistant ID
   * @param {Object} attorneyData - Attorney data from Supabase
   * @returns {Promise<Object>} Updated assistant data
   */
  async updateAssistantConfiguration(assistantId, attorneyData) {
    if (!assistantId) {
      throw new Error('Assistant ID is required to update configuration');
    }

    if (!attorneyData) {
      throw new Error('Attorney data is required to update configuration');
    }

    try {
      // Ensure connection to Vapi MCP server
      const connected = await this.ensureConnection();

      if (!connected) {
        console.warn('VapiAssistantService: Not connected to Vapi MCP server. Returning mock updated assistant.');

        // Return a mock updated assistant for development
        return {
          id: assistantId,
          name: `${attorneyData.firm_name} Assistant`,
          instructions: attorneyData.vapi_instructions,
          firstMessage: attorneyData.welcome_message || `Hello, I'm Scout from ${attorneyData.firm_name}. How can I help you today?`,
          firstMessageMode: "assistant-speaks-first",
          updatedAt: new Date().toISOString(),
          voice: {
            provider: attorneyData.voice_provider || 'playht',
            voiceId: attorneyData.voice_id || 'sarah'
          },
          mock: true
        };
      }

      // First get the current assistant to preserve other settings
      const assistant = await vapiMcpService.getAssistant(assistantId);

      if (!assistant) {
        console.warn(`VapiAssistantService: Assistant with ID ${assistantId} not found. Using mock assistant.`);

        // Return a mock assistant if the real one can't be found
        return {
          id: assistantId,
          name: `${attorneyData.firm_name} Assistant`,
          instructions: attorneyData.vapi_instructions,
          firstMessage: attorneyData.welcome_message || `Hello, I'm Scout from ${attorneyData.firm_name}. How can I help you today?`,
          firstMessageMode: "assistant-speaks-first",
          updatedAt: new Date().toISOString(),
          voice: {
            provider: attorneyData.voice_provider || 'playht',
            voiceId: attorneyData.voice_id || 'sarah'
          },
          mock: true
        };
      }

      // Generate structured data schema from custom fields
      let structuredDataSchema = null;
      if (attorneyData.custom_fields) {
        try {
          // If stored as string, parse it
          const customFields = typeof attorneyData.custom_fields === 'string'
            ? JSON.parse(attorneyData.custom_fields)
            : attorneyData.custom_fields;

          structuredDataSchema = generateStructuredDataSchema(customFields);
        } catch (error) {
          console.error('Error generating schema from custom fields:', error);
        }
      }

      // Create update configuration with new settings
      const updateConfig = {
        ...assistant,
        name: `${attorneyData.firm_name} Assistant`,
        instructions: attorneyData.vapi_instructions || assistant.instructions,
        firstMessage: attorneyData.welcome_message || `Hello, I'm Scout from ${attorneyData.firm_name}. How can I help you today?`,
        firstMessageMode: "assistant-speaks-first",
        llm: {
          ...assistant.llm,
          model: attorneyData.ai_model || assistant.llm.model
        },
        voice: {
          ...assistant.voice,
          provider: attorneyData.voice_provider || assistant.voice.provider || 'playht',
          voiceId: attorneyData.voice_id || assistant.voice.voiceId || 'sarah'
        },
        // Update analysis configuration
        analysis: {
          // Summary configuration
          summary: {
            prompt: attorneyData.summary_prompt || DEFAULT_SUMMARY_PROMPT
          }
        }
      };

      // Add structured data configuration if schema is available
      if (structuredDataSchema) {
        updateConfig.analysis.structuredData = {
          prompt: attorneyData.structured_data_prompt || DEFAULT_STRUCTURED_DATA_PROMPT,
          schema: structuredDataSchema
        };
      }

      // Update the assistant
      const updatedAssistant = await vapiMcpService.updateAssistant(assistantId, updateConfig);

      return updatedAssistant;
    } catch (error) {
      console.error('Error updating assistant configuration:', error);

      // Return a mock updated assistant instead of throwing
      return {
        id: assistantId,
        name: `${attorneyData.firm_name} Assistant`,
        instructions: attorneyData.vapi_instructions,
        firstMessage: `Hello, I'm Scout from ${attorneyData.firm_name}. How can I help you today?`,
        updatedAt: new Date().toISOString(),
        mock: true,
        error: error.message
      };
    }
  }

  /**
   * Update an existing assistant's instructions (legacy method)
   * @param {string} assistantId - Vapi assistant ID
   * @param {string} instructions - New instructions for the assistant
   * @param {string} firstMessage - New first message for the assistant
   * @returns {Promise<Object>} Updated assistant data
   * @deprecated Use updateAssistantConfiguration instead
   */
  async updateAssistantInstructions(assistantId, instructions, firstMessage) {
    if (!assistantId) {
      throw new Error('Assistant ID is required to update instructions');
    }

    try {
      // Ensure connection to Vapi MCP server
      const connected = await this.ensureConnection();

      if (!connected) {
        console.warn('VapiAssistantService: Not connected to Vapi MCP server. Returning mock updated assistant.');

        // Return a mock updated assistant for development
        return {
          id: assistantId,
          instructions: instructions,
          firstMessage: firstMessage,
          firstMessageMode: "assistant-speaks-first",
          updatedAt: new Date().toISOString(),
          voice: {
            provider: 'playht',
            voiceId: 'sarah'
          },
          mock: true
        };
      }

      // First get the current assistant to preserve other settings
      const assistant = await vapiMcpService.getAssistant(assistantId);

      if (!assistant) {
        console.warn(`VapiAssistantService: Assistant with ID ${assistantId} not found. Using mock assistant.`);

        // Return a mock assistant if the real one can't be found
        return {
          id: assistantId,
          instructions: instructions,
          firstMessage: firstMessage,
          firstMessageMode: "assistant-speaks-first",
          updatedAt: new Date().toISOString(),
          voice: {
            provider: 'playht',
            voiceId: 'sarah'
          },
          mock: true
        };
      }

      // Create update configuration with new instructions
      const updateConfig = {
        ...assistant,
        instructions: instructions || assistant.instructions,
        firstMessage: firstMessage || assistant.firstMessage,
        firstMessageMode: "assistant-speaks-first" // Always ensure this is set
      };

      console.log('Updating assistant with config:', {
        assistantId,
        instructions: updateConfig.instructions?.substring(0, 50) + '...',
        firstMessage: updateConfig.firstMessage,
        firstMessageMode: updateConfig.firstMessageMode
      });

      // Update the assistant
      const updatedAssistant = await vapiMcpService.updateAssistant(assistantId, updateConfig);

      return updatedAssistant;
    } catch (error) {
      console.error('Error updating assistant instructions:', error);

      // Return a mock updated assistant instead of throwing
      return {
        id: assistantId,
        instructions: instructions,
        firstMessage: firstMessage,
        firstMessageMode: "assistant-speaks-first",
        updatedAt: new Date().toISOString(),
        voice: {
          provider: 'playht',
          voiceId: 'sarah'
        },
        mock: true,
        error: error.message
      };
    }
  }

  /**
   * Create assistants for all attorneys who don't have one
   * @returns {Promise<Array>} Array of created assistants
   */
  async createAssistantsForAllAttorneys() {
    try {
      // Get all attorneys without assistant IDs
      const { data: attorneys, error } = await supabase
        .from('attorneys')
        .select('*')
        .is('vapi_assistant_id', null);

      if (error) throw error;

      // Create assistants for each attorney
      const results = [];
      for (const attorney of attorneys) {
        try {
          const assistant = await this.createAssistantForAttorney(attorney);
          results.push({
            attorneyId: attorney.id,
            assistantId: assistant.id,
            success: true
          });
        } catch (err) {
          results.push({
            attorneyId: attorney.id,
            error: err.message,
            success: false
          });
        }
      }

      return results;
    } catch (error) {
      console.error('Error creating assistants for all attorneys:', error);
      throw error;
    }
  }
}

// Export a singleton instance
export const vapiAssistantService = new VapiAssistantService();

// Export the class for testing or custom instantiation
export default VapiAssistantService;
